import React, { useState, useEffect } from "react";
import {
  Text,
  Input,
  InputContainer,
  PaymentDropdown,
} from "components/common";
import isKey from "utils/keyboard-events";
import { handleNonIntegerInputs } from "./utils";

const UpdateAccountNumber = ({
  description,
  paymentMethodsNeedUpdate,
  updateDefaultPaymentMethodInfo,
  routingNumber,
  setRoutingNumber,
  accountNumber,
  setAccountNumber,
  confirmAccountNumber,
  setConfirmAccountNumber,
  bankVerificationError,
  loading,
  setLoading,
  setDrawerOpen,
  resetAccountUpdateErrorFields,
  setPaymentMethodsNeedUpdate,
  setBankVerificationError,
  setAccountUpdated,
  accountUpdateSuccess,
  setAccountUpdateSuccess,
  selectedPaymentMethodId,
  setSelectedPaymentMethodId,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  onDrawerActionsComplete = () => {},
}) => {
  const [shouldShowAccountPass, setShouldShowAccountPass] = useState(false);
  const [shouldShowConfirmAccountPass, setShouldShowConfirmAccountPass] =
    useState(false);

  const resetAccountUpdateFields = () => {
    setAccountNumber(null);
    setConfirmAccountNumber(null);
    setRoutingNumber(null);

    setAccountUpdateSuccess(true);
    setLoading(false);
    if (paymentMethodsNeedUpdate.length === 0) {
      onDrawerActionsComplete();
      setDrawerOpen(false);
    }
  };
  const validateAccountUpdateResult = (result) => {
    if (!result.success) {
      setLoading(false);
      setBankVerificationError(
        "Unable to verify banking information, please try again.",
      );
      throw {
        message: "Unable to verify banking information, please try again.",
      };
    }
  };
  const verifyAccountFields = () => {
    if (!accountNumber || !confirmAccountNumber || !routingNumber) {
      setBankVerificationError("Unable to verify bank: missing fields");
      setLoading(false);
      throw { message: "Unable to verify bank: missing fields" };
    }
    if (accountNumber !== confirmAccountNumber) {
      setBankVerificationError("Account numbers do not match!");
      setLoading(false);
      throw { message: "Account numbers do not match!" };
    }
  };
  const cardAccountUpdate = async () => {
    try {
      resetAccountUpdateErrorFields();
      verifyAccountFields();
      const result = await updateDefaultPaymentMethodInfo(
        selectedPaymentMethodId,
      );
      await validateAccountUpdateResult(result);
    } catch (e) {
      return e.message;
    }
    if (paymentMethodsNeedUpdate.length >= 1) {
      const selectedPaymentMethods = paymentMethodsNeedUpdate;
      const accountId = selectedPaymentMethods.findIndex((a) => {
        return a.id === selectedPaymentMethodId;
      });
      selectedPaymentMethods.splice(accountId, 1);
      setPaymentMethodsNeedUpdate(selectedPaymentMethods);
      if (selectedPaymentMethods.length > 0) {
        setSelectedPaymentMethodId(selectedPaymentMethods[0].id);
        setSelectedPaymentMethod(selectedPaymentMethods[0]);
      } else {
        setAccountUpdated(true);
      }
    }
    resetAccountUpdateFields();
  };
  const handleSelectedAccountUpdate = (data = {}) => {
    setSelectedPaymentMethodId(data.id);
  };

  const renderUpdateAccountButton = () => (
    <button
      type="button"
      className="btn btn-green mt-3 px-10 py-2 w-full"
      onClick={cardAccountUpdate}
      disabled={loading}
    >
      Update Account
    </button>
  );

  return (
    <div className="flex flex-col p-4 text-center sm:p-10">
      <div className="flex-1 h-full align-center pt-5">
        <hr />
        <Text className="mb-2 pt-3" size="text-2xl">
          Verify Bank Information
        </Text>

        <span className="mb-3">{description}</span>

        <Text className="text-base mt-3 mb-1">Payment Method</Text>
        <PaymentDropdown
          paymentMethods={paymentMethodsNeedUpdate}
          defaultElementId={selectedPaymentMethodId || null}
          selectedElement={selectedPaymentMethod}
          didSelect={handleSelectedAccountUpdate}
          dropDownClassName="Dropdown-paymentDrawer"
        />
        <Text className="text-base mt-3 mb-1">Routing Number*</Text>
        <InputContainer>
          <Input
            alt="routing-number"
            value={routingNumber || ""}
            onKeyDown={(e) => handleNonIntegerInputs(e, isKey)}
            onChange={(e) => {
              if (e.target.value.length < 17) {
                setRoutingNumber(e.target.value);
              }
            }}
            placeholder="xxxxxxxxxx"
            type="number"
            pattern="[0-9]*"
          />
        </InputContainer>
        <Text className="text-base mt-3 mb-1">Account Number*</Text>
        <InputContainer>
          <Input
            alt="account-number"
            value={accountNumber || ""}
            onKeyDown={(e) => handleNonIntegerInputs(e, isKey)}
            onChange={(e) => {
              if (e.target.value.length < 17) {
                setAccountNumber(e.target.value);
              }
            }}
            type={shouldShowAccountPass ? "number" : "password"}
            placeholder="xxxxxxxxxx"
            pattern="[0-9]*"
          />
          {accountNumber?.length && (
            <i
              onClick={() => setShouldShowAccountPass((prev) => !prev)}
              role="button"
              className="fas fa-eye my-auto"
            />
          )}
        </InputContainer>
        <Text className="text-base mt-3 mb-1">Confirm Account Number*</Text>
        <InputContainer>
          <Input
            alt="confirm-account-number"
            value={confirmAccountNumber || ""}
            onKeyDown={(e) => handleNonIntegerInputs(e, isKey)}
            onChange={(e) => {
              if (e.target.value.length < 17) {
                setConfirmAccountNumber(e.target.value);
              }
            }}
            type={shouldShowConfirmAccountPass ? "number" : "password"}
            placeholder="xxxxxxxxxx"
            pattern="[0-9]*"
          />
          {confirmAccountNumber?.length && (
            <i
              onClick={() => setShouldShowConfirmAccountPass((prev) => !prev)}
              role="button"
              className="fas fa-eye my-auto"
            />
          )}
        </InputContainer>
      </div>
      <div className="lg flex w-full mt-2">{renderUpdateAccountButton()}</div>
      {bankVerificationError && (
        <Text className="text-base text-center text-red-500 mt-3 mb-1">
          {bankVerificationError}
        </Text>
      )}
      {accountUpdateSuccess && (
        <div className="flex flex-col p-3 text-center">
          <div className="duration-500 ease-in-out">
            {/* <Confetti colors={["#26E3BA", "#011516", "#e2e8f0"]} /> */}
            <div className="w-full mb-4 text-xl font-bold text-gray-primary">
              Account Update Successful.
            </div>
          </div>
          <div className="w-full mb-6 text-2xl">
            Please update remaining accounts as well.
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateAccountNumber;
